import * as React from "react";
import { connect } from "react-redux";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";

import { AnsweredQuestionWithId } from "@teamprevent/common/model";
import { clearQuestionnaire, submitInvalidQuestionnaire } from "@teamprevent/common/questionnaires/actions";
import { goToParent } from "@teamprevent/common/routing";

import { FollowUpTasks } from "../../immunisationReview/components/FollowUpTasks";
import { OhCase } from "../../crud/ohCase";
import { FollowUpTaskModel } from "../../immunisationReview/model";
import { ApplicationState } from "../../applicationState";

import { submitVaccinationConsultation } from "../actions";
import { ohCaseSelector, routeIdSelector as ohCaseIdSelector } from "../../crud/ohCase/selectors";
import { immunisationTypesSelector } from "../../crud/immunisationType/selectors";
import { ImmunisationType, loadImmunisationTypes } from "../../crud/immunisationType";

import { VaccinationConsultationNotes, QUESTIONNAIRE_KEY } from "./VaccinationConsultationNotes";
import { VaccinationConsultationNotesPrint } from "./VaccinationConsultationNotesPrint";

interface FollowUpTask {
    task: FollowUpTaskModel;
    isValid: boolean;
}

interface StateProps {
    ohCase: OhCase;
    ohCaseId: string;
    taskId: string;
    immunisationTypes: ImmunisationType[];
    pathname?: string;
}

interface DispatchProps {
    submitQuestionnaire: (
        ohCaseId: string,
        taskId: string,
        questions: AnsweredQuestionWithId[],
        tasks: FollowUpTaskModel[],
        immunisationTypeTaskIds: string[]
    ) => Promise<void>;
    submitInvalidQuestionnaire: () => void;
    navigateBack: () => void;
    loadImmunisationTypes: () => void;
    clearQuestionnaire: () => void;
}

interface State {
    submitted: boolean;
    tasks: FollowUpTask[];
}

type Props = StateProps & DispatchProps;

export class ImmunisationVaccinationConsultationNotesUnconnected extends React.Component<Props, State> {
    public state: State = { submitted: false, tasks: [{ task: { immunisationTypeTasks: [], taskType: "" }, isValid: false }] };

    public componentDidMount() {
        this.props.clearQuestionnaire();

        if (this.props.immunisationTypes.length === 0) {
            this.props.loadImmunisationTypes();
        }
    }

    public render() {
        const tasksSection = this.props.immunisationTypes.length > 0 &&
            (<FollowUpTasks
                immunisationTypes={this.props.immunisationTypes.map(i => i.id)}
                onChange={this.onTasksChanged}
                showErrors={this.state.submitted}
                tasks={this.state.tasks}
                assignmentOptionRequired={true}
            />);
        return (
            <>
                {this.props.pathname?.indexOf("immunisationVaccinationConsultationNotesPrint") > 0 ?
                    (<VaccinationConsultationNotesPrint
                        tasksSectionValid={this.tasksValid()}
                        tasksSection={tasksSection}
                        submitQuestionnaire={this.submitQuestionnaire}
                    />) :
                    (<VaccinationConsultationNotes
                        tasksSectionValid={this.tasksValid()}
                        tasksSection={tasksSection}
                        submitQuestionnaire={this.submitQuestionnaire}
                    />)
                }
            </>
        );
    }

    private onTasksChanged = (tasks: FollowUpTask[]) => this.setState({ tasks });

    private tasksValid = () => this.state.tasks.length !== 0 && this.state.tasks.every(t => t.isValid);

    private submitQuestionnaire = async (questions: AnsweredQuestionWithId[], immunisationTypeTaskIds: string[]) => {
        this.setState({ submitted: true });

        if (!this.tasksValid()) {
            this.props.submitInvalidQuestionnaire();
            return;
        }

        await this.props.submitQuestionnaire(
            this.props.ohCaseId,
            this.props.taskId,
            questions,
            this.state.tasks.map(t => t.task),
            immunisationTypeTaskIds);
        this.props.navigateBack();
    };
}

const mapStateToProps = (state: ApplicationState): StateProps => ({
    ohCase: ohCaseSelector(state),
    ohCaseId: ohCaseIdSelector(state),
    taskId: state.router.params.taskId,
    immunisationTypes: immunisationTypesSelector(state),
    pathname: state.router.pathname
});

const mapDispatchToProps = (dispatch: ThunkDispatch<ApplicationState, undefined, Action>): DispatchProps => ({
    submitQuestionnaire: (
        ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], tasks: FollowUpTaskModel[], immunisationTypeTaskIds: string[]
    ) =>
        dispatch(submitVaccinationConsultation(ohCaseId, taskId, questions, QUESTIONNAIRE_KEY, tasks, immunisationTypeTaskIds)),
    submitInvalidQuestionnaire: () => dispatch(submitInvalidQuestionnaire(QUESTIONNAIRE_KEY, true)),
    navigateBack: () => dispatch(goToParent()),
    loadImmunisationTypes: () => dispatch(loadImmunisationTypes()),
    clearQuestionnaire: () => dispatch(clearQuestionnaire(QUESTIONNAIRE_KEY)),
});

export const ImmunisationVaccinationConsultationNotes = connect(mapStateToProps, mapDispatchToProps)(ImmunisationVaccinationConsultationNotesUnconnected);
